import React, { useState, useEffect } from 'react';
import { FaRupeeSign, FaDownload, FaRedo, FaPlus, FaTrash, FaFileInvoice } from 'react-icons/fa';
import { formatCurrency } from '../utils/calculations';
import { generateSalarySlipPDF } from '../utils/pdfGenerator';
import SEO from '../components/SEO';

const SalaryCalculator = () => {
  // Employee Details
  const [employeeDetails, setEmployeeDetails] = useState({
    name: '',
    employeeId: '',
    designation: '',
    department: '',
    panNumber: '',
    bankAccount: '',
    joiningDate: '',
    payPeriod: new Date().toISOString().slice(0, 7), // YYYY-MM format
  });

  // Earnings
  const [basicSalary, setBasicSalary] = useState(50000);
  const [hra, setHra] = useState(20000);
  const [da, setDa] = useState(5000);
  const [conveyanceAllowance, setConveyanceAllowance] = useState(1600);
  const [medicalAllowance, setMedicalAllowance] = useState(1250);
  const [specialAllowance, setSpecialAllowance] = useState(10000);
  
  // Custom Earnings
  const [customEarnings, setCustomEarnings] = useState([]);
  
  // Deductions
  const [pfEmployee, setPfEmployee] = useState(0);
  // eslint-disable-next-line no-unused-vars
  const [pfEmployer, setPfEmployer] = useState(0);
  const [esi, setEsi] = useState(0);
  const [professionalTax, setProfessionalTax] = useState(200);
  const [tds, setTds] = useState(0);
  const [loanDeduction, setLoanDeduction] = useState(0);
  
  // Custom Deductions
  const [customDeductions, setCustomDeductions] = useState([]);
  
  // Settings
  const [calculatePF, setCalculatePF] = useState(true);
  const [calculateESI, setCalculateESI] = useState(true);
  const [pfPercentage, setPfPercentage] = useState(12);
  const [esiPercentage, setEsiPercentage] = useState(0.75);
  
  // Results
  const [result, setResult] = useState(null);

  // Company Details for Salary Slip
  // eslint-disable-next-line no-unused-vars
  const [companyDetails, setCompanyDetails] = useState({
    name: 'ABC Company Pvt. Ltd.',
    address: '123 Business Park, Mumbai, Maharashtra - 400001',
    logo: '',
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    calculateSalary();
  }, [basicSalary, hra, da, conveyanceAllowance, medicalAllowance, specialAllowance, 
      customEarnings, pfEmployee, esi, professionalTax, tds, loanDeduction, 
      customDeductions, calculatePF, calculateESI, pfPercentage, esiPercentage]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const calculateSalary = () => {
    // Calculate total earnings
    const basic = Number(basicSalary) || 0;
    const hraVal = Number(hra) || 0;
    const daVal = Number(da) || 0;
    const convVal = Number(conveyanceAllowance) || 0;
    const medVal = Number(medicalAllowance) || 0;
    const specVal = Number(specialAllowance) || 0;
    const profTax = Number(professionalTax) || 0;
    const tdsVal = Number(tds) || 0;
    const loanDed = Number(loanDeduction) || 0;
    
    const customEarningsTotal = customEarnings.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const grossSalary = basic + hraVal + daVal + convVal + medVal + specVal + customEarningsTotal;
    
    // Calculate PF
    let employeePF = 0;
    let employerPF = 0;
    if (calculatePF) {
      const pfBasic = basic + daVal;
      employeePF = Math.round((pfBasic * pfPercentage) / 100);
      employerPF = Math.round((pfBasic * pfPercentage) / 100);
      setPfEmployee(employeePF);
      setPfEmployer(employerPF);
    }
    
    // Calculate ESI (applicable if gross <= 21000)
    let esiDeduction = 0;
    if (calculateESI && grossSalary <= 21000) {
      esiDeduction = Math.round((grossSalary * esiPercentage) / 100);
      setEsi(esiDeduction);
    } else {
      setEsi(0);
    }
    
    // Calculate total deductions
    const customDeductionsTotal = customDeductions.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    const totalDeductions = employeePF + esiDeduction + profTax + tdsVal + loanDed + customDeductionsTotal;
    
    // Calculate net salary
    const netSalary = grossSalary - totalDeductions;
    
    // Earnings breakdown
    const earningsBreakdown = [
      { name: 'Basic Salary', amount: basic },
      { name: 'House Rent Allowance (HRA)', amount: hraVal },
      { name: 'Dearness Allowance (DA)', amount: daVal },
      { name: 'Conveyance Allowance', amount: convVal },
      { name: 'Medical Allowance', amount: medVal },
      { name: 'Special Allowance', amount: specVal },
      ...customEarnings.filter(e => e.name && e.amount).map(e => ({ name: e.name, amount: Number(e.amount) })),
    ].filter(e => e.amount > 0);
    
    // Deductions breakdown
    const deductionsBreakdown = [
      { name: 'Provident Fund (Employee)', amount: employeePF },
      { name: 'ESI (Employee)', amount: esiDeduction },
      { name: 'Professional Tax', amount: profTax },
      { name: 'Tax Deducted at Source (TDS)', amount: tdsVal },
      { name: 'Loan/Advance Deduction', amount: loanDed },
      ...customDeductions.filter(d => d.name && d.amount).map(d => ({ name: d.name, amount: Number(d.amount) })),
    ].filter(d => d.amount > 0);
    
    setResult({
      grossSalary,
      totalDeductions,
      netSalary,
      earningsBreakdown,
      deductionsBreakdown,
      employeePF,
      employerPF,
      esiDeduction,
      ctc: grossSalary + employerPF + (calculateESI && grossSalary <= 21000 ? Math.round((grossSalary * 3.25) / 100) : 0),
    });
  };

  const addCustomEarning = () => {
    setCustomEarnings([...customEarnings, { name: '', amount: 0 }]);
  };

  const removeCustomEarning = (index) => {
    setCustomEarnings(customEarnings.filter((_, i) => i !== index));
  };

  const updateCustomEarning = (index, field, value) => {
    const updated = [...customEarnings];
    updated[index][field] = field === 'amount' ? Number(value) : value;
    setCustomEarnings(updated);
  };

  const addCustomDeduction = () => {
    setCustomDeductions([...customDeductions, { name: '', amount: 0 }]);
  };

  const removeCustomDeduction = (index) => {
    setCustomDeductions(customDeductions.filter((_, i) => i !== index));
  };

  const updateCustomDeduction = (index, field, value) => {
    const updated = [...customDeductions];
    updated[index][field] = field === 'amount' ? Number(value) : value;
    setCustomDeductions(updated);
  };

  const handleDownloadSalarySlip = () => {
    if (!result) return;
    
    generateSalarySlipPDF({
      employeeDetails,
      companyDetails,
      result,
      payPeriod: employeeDetails.payPeriod,
    });
  };

  const handleReset = () => {
    setBasicSalary(50000);
    setHra(20000);
    setDa(5000);
    setConveyanceAllowance(1600);
    setMedicalAllowance(1250);
    setSpecialAllowance(10000);
    setCustomEarnings([]);
    setProfessionalTax(200);
    setTds(0);
    setLoanDeduction(0);
    setCustomDeductions([]);
    setEmployeeDetails({
      name: '',
      employeeId: '',
      designation: '',
      department: '',
      panNumber: '',
      bankAccount: '',
      joiningDate: '',
      payPeriod: new Date().toISOString().slice(0, 7),
    });
  };

  const formatMonth = (dateStr) => {
    if (!dateStr) return '';
    const [year, month] = dateStr.split('-');
    const date = new Date(year, month - 1);
    return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="calculator-page fade-in">
      <SEO 
        title="Salary Calculator"
        description="Free Salary Calculator India - Calculate in-hand salary, CTC breakdown, PF, tax deductions. Monthly & yearly salary computation."
        keywords="salary calculator, salary calculator India, CTC calculator, in-hand salary calculator, take home salary calculator, salary slip generator"
        canonicalPath="/salary"
      />
      <div className="calculator-header">
        <h1>Salary Calculator</h1>
        <p>Calculate salary components and generate professional salary slips</p>
      </div>

      <div className="calculator-container">
        {/* Left Column - Inputs */}
        <div className="calculator-inputs">
          {/* Employee Details Section */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: 'var(--surface)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <h3 style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaFileInvoice /> Employee Details (for Salary Slip)
            </h3>
            <div className="grid-2-cols">
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Employee Name</label>
                <input
                  type="text"
                  value={employeeDetails.name}
                  onChange={(e) => setEmployeeDetails({...employeeDetails, name: e.target.value})}
                  placeholder="John Doe"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Employee ID</label>
                <input
                  type="text"
                  value={employeeDetails.employeeId}
                  onChange={(e) => setEmployeeDetails({...employeeDetails, employeeId: e.target.value})}
                  placeholder="EMP001"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Designation</label>
                <input
                  type="text"
                  value={employeeDetails.designation}
                  onChange={(e) => setEmployeeDetails({...employeeDetails, designation: e.target.value})}
                  placeholder="Software Engineer"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Department</label>
                <input
                  type="text"
                  value={employeeDetails.department}
                  onChange={(e) => setEmployeeDetails({...employeeDetails, department: e.target.value})}
                  placeholder="IT"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>PAN Number</label>
                <input
                  type="text"
                  value={employeeDetails.panNumber}
                  onChange={(e) => setEmployeeDetails({...employeeDetails, panNumber: e.target.value.toUpperCase()})}
                  placeholder="ABCDE1234F"
                  maxLength={10}
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Bank Account</label>
                <input
                  type="text"
                  value={employeeDetails.bankAccount}
                  onChange={(e) => setEmployeeDetails({...employeeDetails, bankAccount: e.target.value})}
                  placeholder="XXXX XXXX 1234"
                />
              </div>
              <div className="input-group" style={{ marginBottom: '0' }}>
                <label>Pay Period</label>
                <input
                  type="month"
                  value={employeeDetails.payPeriod}
                  onChange={(e) => setEmployeeDetails({...employeeDetails, payPeriod: e.target.value})}
                />
              </div>
            </div>
          </div>

          {/* Earnings Section */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: '#ecfdf5', borderRadius: '12px', border: '1px solid #10b981' }}>
            <h3 style={{ marginBottom: '16px', color: '#059669' }}>💰 Earnings</h3>
            
            <div className="input-group">
              <label>Basic Salary (₹)</label>
              <div className="input-with-icon">
                <FaRupeeSign className="icon" />
                <input
                  type="number"
                  value={basicSalary === '' ? '' : basicSalary}
                  onChange={(e) => setBasicSalary(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
            </div>

            <div className="grid-2-cols">
              <div className="input-group">
                <label>HRA (₹)</label>
                <input
                  type="number"
                  value={hra === '' ? '' : hra}
                  onChange={(e) => setHra(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
              <div className="input-group">
                <label>DA (₹)</label>
                <input
                  type="number"
                  value={da === '' ? '' : da}
                  onChange={(e) => setDa(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
              <div className="input-group">
                <label>Conveyance (₹)</label>
                <input
                  type="number"
                  value={conveyanceAllowance === '' ? '' : conveyanceAllowance}
                  onChange={(e) => setConveyanceAllowance(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
              <div className="input-group">
                <label>Medical (₹)</label>
                <input
                  type="number"
                  value={medicalAllowance === '' ? '' : medicalAllowance}
                  onChange={(e) => setMedicalAllowance(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
              <div className="input-group" style={{ gridColumn: 'span 2' }}>
                <label>Special Allowance (₹)</label>
                <input
                  type="number"
                  value={specialAllowance === '' ? '' : specialAllowance}
                  onChange={(e) => setSpecialAllowance(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
            </div>

            {/* Custom Earnings */}
            {customEarnings.map((earning, index) => (
              <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'flex-end' }}>
                <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label>Name</label>
                  <input
                    type="text"
                    value={earning.name}
                    onChange={(e) => updateCustomEarning(index, 'name', e.target.value)}
                    placeholder="Bonus, Incentive, etc."
                  />
                </div>
                <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label>Amount (₹)</label>
                  <input
                    type="number"
                    value={earning.amount}
                    onChange={(e) => updateCustomEarning(index, 'amount', e.target.value)}
                    min="0"
                  />
                </div>
                <button 
                  onClick={() => removeCustomEarning(index)}
                  style={{ padding: '10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  <FaTrash />
                </button>
              </div>
            ))}
            
            <button 
              onClick={addCustomEarning}
              style={{ width: '100%', padding: '10px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <FaPlus /> Add Custom Earning
            </button>
          </div>

          {/* Deductions Section */}
          <div className="section-card" style={{ marginBottom: '20px', padding: '20px', background: '#fef2f2', borderRadius: '12px', border: '1px solid #ef4444' }}>
            <h3 style={{ marginBottom: '16px', color: '#dc2626' }}>📉 Deductions</h3>
            
            {/* PF Settings */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '8px' }}>
              <input
                type="checkbox"
                id="calculatePF"
                checked={calculatePF}
                onChange={(e) => setCalculatePF(e.target.checked)}
                style={{ width: '20px', height: '20px' }}
              />
              <label htmlFor="calculatePF" style={{ flex: 1 }}>Auto Calculate PF</label>
              <input
                type="number"
                value={pfPercentage}
                onChange={(e) => setPfPercentage(Number(e.target.value))}
                min="0"
                max="100"
                style={{ width: '60px' }}
                disabled={!calculatePF}
              />
              <span>%</span>
            </div>

            {/* ESI Settings */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '8px' }}>
              <input
                type="checkbox"
                id="calculateESI"
                checked={calculateESI}
                onChange={(e) => setCalculateESI(e.target.checked)}
                style={{ width: '20px', height: '20px' }}
              />
              <label htmlFor="calculateESI" style={{ flex: 1 }}>Auto Calculate ESI (if applicable)</label>
              <input
                type="number"
                value={esiPercentage}
                onChange={(e) => setEsiPercentage(Number(e.target.value))}
                min="0"
                max="100"
                step="0.25"
                style={{ width: '60px' }}
                disabled={!calculateESI}
              />
              <span>%</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div className="input-group">
                <label>Professional Tax (₹)</label>
                <input
                  type="number"
                  value={professionalTax === '' ? '' : professionalTax}
                  onChange={(e) => setProfessionalTax(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
              <div className="input-group">
                <label>TDS (₹)</label>
                <input
                  type="number"
                  value={tds === '' ? '' : tds}
                  onChange={(e) => setTds(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
              <div className="input-group" style={{ gridColumn: 'span 2' }}>
                <label>Loan/Advance Deduction (₹)</label>
                <input
                  type="number"
                  value={loanDeduction === '' ? '' : loanDeduction}
                  onChange={(e) => setLoanDeduction(e.target.value === '' ? '' : Number(e.target.value))}
                  min="0"
                />
              </div>
            </div>

            {/* Custom Deductions */}
            {customDeductions.map((deduction, index) => (
              <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '12px', alignItems: 'flex-end' }}>
                <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label>Name</label>
                  <input
                    type="text"
                    value={deduction.name}
                    onChange={(e) => updateCustomDeduction(index, 'name', e.target.value)}
                    placeholder="Insurance, etc."
                  />
                </div>
                <div className="input-group" style={{ flex: 1, marginBottom: 0 }}>
                  <label>Amount (₹)</label>
                  <input
                    type="number"
                    value={deduction.amount}
                    onChange={(e) => updateCustomDeduction(index, 'amount', e.target.value)}
                    min="0"
                  />
                </div>
                <button 
                  onClick={() => removeCustomDeduction(index)}
                  style={{ padding: '10px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                >
                  <FaTrash />
                </button>
              </div>
            ))}
            
            <button 
              onClick={addCustomDeduction}
              style={{ width: '100%', padding: '10px', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
            >
              <FaPlus /> Add Custom Deduction
            </button>
          </div>

          <div className="button-group">
            <button className="btn btn-outline" onClick={handleReset}>
              <FaRedo style={{ marginRight: '8px' }} /> Reset
            </button>
            <button className="btn btn-secondary" onClick={handleDownloadSalarySlip}>
              <FaDownload style={{ marginRight: '8px' }} /> Download Salary Slip
            </button>
          </div>
        </div>

        {/* Right Column - Results */}
        <div className="calculator-results">
          {result && (
            <>
              <div className="result-summary" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                <h3>Net Salary (Take Home)</h3>
                <div className="amount">{formatCurrency(result.netSalary)}</div>
                <small style={{ opacity: 0.9 }}>for {formatMonth(employeeDetails.payPeriod)}</small>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                <div className="result-item" style={{ background: '#ecfdf5', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Gross Salary</span>
                  <span className="value" style={{ color: '#10b981', fontSize: '1.2em' }}>{formatCurrency(result.grossSalary)}</span>
                </div>
                <div className="result-item" style={{ background: '#fef2f2', textAlign: 'center', padding: '16px' }}>
                  <span className="label">Total Deductions</span>
                  <span className="value" style={{ color: '#ef4444', fontSize: '1.2em' }}>{formatCurrency(result.totalDeductions)}</span>
                </div>
              </div>

              <div className="result-item" style={{ background: '#eff6ff', marginBottom: '20px' }}>
                <span className="label">Cost to Company (CTC)</span>
                <span className="value" style={{ color: '#2563eb' }}>{formatCurrency(result.ctc)}</span>
              </div>

              {/* Earnings Breakdown */}
              <div className="breakup-section">
                <h4 style={{ color: '#10b981' }}>💰 Earnings Breakdown</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.earningsBreakdown.map((item, index) => (
                      <tr key={index}>
                        <td>{item.name}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 'bold', background: '#d1fae5' }}>
                      <td>Total Earnings</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(result.grossSalary)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Deductions Breakdown */}
              <div className="breakup-section" style={{ marginTop: '20px' }}>
                <h4 style={{ color: '#ef4444' }}>📉 Deductions Breakdown</h4>
                <table className="tax-slab-table">
                  <thead>
                    <tr>
                      <th>Component</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.deductionsBreakdown.map((item, index) => (
                      <tr key={index}>
                        <td>{item.name}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                    <tr style={{ fontWeight: 'bold', background: '#fecaca' }}>
                      <td>Total Deductions</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(result.totalDeductions)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Employer Contributions */}
              {(result.employerPF > 0) && (
                <div className="breakup-section" style={{ marginTop: '20px' }}>
                  <h4 style={{ color: '#6366f1' }}>🏢 Employer Contributions</h4>
                  <div className="result-details">
                    <div className="result-item">
                      <span className="label">Employer PF Contribution</span>
                      <span className="value">{formatCurrency(result.employerPF)}</span>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="info-box">
        <h4>Salary Components Guide</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '12px' }}>
          <div>
            <strong>Common Earnings</strong>
            <ul style={{ marginTop: '8px' }}>
              <li><strong>Basic:</strong> 40-50% of CTC</li>
              <li><strong>HRA:</strong> 40-50% of Basic (metro cities)</li>
              <li><strong>DA:</strong> Dearness Allowance</li>
              <li><strong>Conveyance:</strong> ₹1,600/month (tax-free)</li>
              <li><strong>Medical:</strong> ₹1,250/month (tax-free)</li>
            </ul>
          </div>
          <div>
            <strong>Common Deductions</strong>
            <ul style={{ marginTop: '8px' }}>
              <li><strong>PF:</strong> 12% of (Basic + DA)</li>
              <li><strong>ESI:</strong> 0.75% if gross ≤ ₹21,000</li>
              <li><strong>PT:</strong> Varies by state (₹200/month max)</li>
              <li><strong>TDS:</strong> Based on income tax slab</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalaryCalculator;
